import type { AboTrancheRowExtendedType, AboTrancheRowType, SortOrder } from '../../types';

/** Module-level cache for parsed filter array values */
const parsedArrays: Record<string, string[]> = {};

export const notSortableCols = ['all', 'kid', 'factsheet'];

export const detailFilterBooleanVal = ['institutional'];

/**
 * Compute the tri-state (0 | 1 | 2) for a column across all tranches.
 * 1 = all checked, 2 = some checked, 0 = none checked.
 */
export function getHeaderColDocState(col: string, documentsSubscribed: Record<string, Record<string, unknown>>): 0 | 1 | 2 {
  const allColSliced = Object.values(documentsSubscribed).map((docs) => Number(docs[col]));

  if (allColSliced.length === 0) {
    return 0;
  }

  if (allColSliced.every((doc) => doc === 1)) {
    return 1;
  }
  if (allColSliced.includes(1) || allColSliced.includes(2)) {
    return 2;
  }
  return 0;
}

/**
 * Compute the "all" tri-state for a set of document values.
 * 1 = all true, 2 = some true, 0 = none true.
 */
export function computeAllState(docs: Record<string, unknown>): 0 | 1 | 2 {
  const values = Object.values(docs);
  if (values.every((v) => v === true)) {
    return 1;
  }
  if (values.includes(true)) {
    return 2;
  }
  return 0;
}

/**
 * Build the header documents subscribed state from per-tranche data.
 */
export function computeHeaderDocumentsSubscribed(keys: string[], documentsSubscribed: Record<string, Record<string, unknown>>): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  for (const key of keys) {
    result[key] = getHeaderColDocState(key, documentsSubscribed) === 1;
  }
  return result;
}

/**
 * Parse a filter value that may be a JSON array string like "[\"a\",\"b\"]".
 * Results are cached in a module-level map.
 */
export function getParsedFilterArrayValue(filterValue: string): string | string[] {
  if (parsedArrays[filterValue]) {
    return parsedArrays[filterValue];
  }

  if (filterValue.startsWith('[') && filterValue.endsWith(']')) {
    try {
      parsedArrays[filterValue] = JSON.parse(filterValue);
      return parsedArrays[filterValue];
    } catch {
      return filterValue;
    }
  }

  return filterValue;
}

/**
 * Compare a filter value against an object field, respecting field type.
 */
export function compareByFieldType(fieldName: string, value: string, obj: Record<string, unknown>): boolean {
  const fieldTypes: Record<string, string> = {
    institutional: 'boolean',
    distribution: 'array',
  };

  let useValue: string | string[] | null = value;

  switch (fieldTypes[fieldName]) {
    case 'boolean':
      useValue = value === 'true' || value === '1' ? '1' : value === 'false' || value === '0' ? '0' : null;
      break;
    case 'array':
      useValue = getParsedFilterArrayValue(value);
      break;
    default:
      useValue = value.toLowerCase();
      break;
  }

  if (Array.isArray(useValue)) {
    return obj[fieldName] != null && useValue.includes(obj[fieldName] as string);
  }

  let checkValue = (obj[fieldName] as string) || '';
  checkValue = typeof checkValue === 'string' ? checkValue.toLowerCase() : checkValue;
  return useValue != null && checkValue.includes(useValue);
}

/**
 * Apply a single filter group to a tranche row.
 * logicalOr=true means any column match passes; false means all must match.
 */
export function filterColumn(filterInfo: Record<string, string[]>, tranche: Record<string, unknown>, logicalOr: boolean): boolean {
  const keys = Object.keys(filterInfo);
  if (!keys.length) {
    return true;
  }

  return keys.reduce<boolean>((filterStatus, columnName) => {
    const filterValues = filterInfo[columnName].filter((item: string) => item !== '');
    if (!filterValues.length) {
      return true;
    }

    const newFilterStatus = filterValues.reduce((innerFilterStatus: boolean, columnValue: string) => {
      if (innerFilterStatus) {
        return innerFilterStatus;
      }
      if (columnValue === '') {
        return innerFilterStatus;
      }
      return compareByFieldType(columnName, columnValue, tranche);
    }, false);

    return logicalOr ? newFilterStatus || filterStatus : newFilterStatus && filterStatus;
  }, !logicalOr);
}

/**
 * Sort comparator that pushes null values to the bottom.
 */
export function nullBottomSortFn(sortCol: string, sortOrder: SortOrder, a: Record<string, unknown>, b: Record<string, unknown>): number {
  if (!(sortCol in a) || !(sortCol in b)) {
    return 0;
  }
  if (a[sortCol] === null) {
    return 1;
  }
  if (b[sortCol] === null) {
    return -1;
  }
  return (a[sortCol] as number) > (b[sortCol] as number) ? sortOrder : -sortOrder;
}

/**
 * Decorate tranche rows with computed document subscription fields.
 */
export function decorateAboTranches(aboTranches: AboTrancheRowType[], documentsSubscribed: Record<string, Record<string, unknown>>, excludedColsInOtherDocs: string[]): AboTrancheRowExtendedType[] {
  return aboTranches.map((tranche) => {
    const saneDocuments = documentsSubscribed[tranche.isin as string];
    if (!saneDocuments) {
      return tranche as AboTrancheRowExtendedType;
    }

    const { all, kid, factsheet } = saneDocuments as Record<string, unknown>;

    // Build the "other documents" map (the data the dropdown reads and writes) from every
    // document key that isn't excluded. kid/factsheet are excluded on desktop because they
    // have dedicated columns, but on mobile they only live inside this dropdown and must be
    // included here. Iterating saneDocuments directly (rather than a rest object that always
    // omits kid/factsheet) ensures their checked state is reflected and toggling works.
    const otherDocs: Record<string, unknown> = {};
    for (const key of Object.keys(saneDocuments)) {
      if (key !== 'all' && !excludedColsInOtherDocs.includes(key)) {
        otherDocs[key] = saneDocuments[key];
      }
    }

    return {
      ...tranche,
      kid,
      factsheet,
      other: otherDocs,
      all,
      otherall: computeAllState(otherDocs),
      subscribed: Object.values(saneDocuments).includes(true) ? 'true' : 'false',
    } as AboTrancheRowExtendedType;
  });
}
